// ─────────────────────────────────────────────
//  IndicesBar.js — Live market indices widget
//  Data via api.js → Yahoo Finance (no key needed)
// ─────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from "react";
import { fetchIndices } from "../../config/api";
import "./IndicesBar.css";

const PLACEHOLDER = [
  { label: "NIFTY 50",   loading: true },
  { label: "SENSEX",     loading: true },
  { label: "NIFTY BANK", loading: true },
  { label: "NIFTY IT",   loading: true },
];

export default function IndicesBar() {
  const [indices,     setIndices]     = useState(PLACEHOLDER);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadIndices = useCallback(async () => {
    try {
      const data = await fetchIndices();
      setIndices(data.map((d) => ({ ...d, loading: false })));
      setLastUpdated(new Date());
    } catch (e) {
      console.error("IndicesBar:", e);
      setIndices((prev) => prev.map((p) => ({ ...p, loading: false, error: true })));
    }
  }, []);

  useEffect(() => {
    loadIndices();
    const id = setInterval(loadIndices, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [loadIndices]);

  return (
    <div className="ib-wrap">
      <div className="ib-header">
        <span className="ib-title">Market Indices</span>
        <span className="ib-updated">
          {lastUpdated
            ? `Updated ${lastUpdated.toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })}`
            : "Loading…"}
        </span>
        <button className="ib-refresh" onClick={loadIndices} title="Refresh">
          ↻
        </button>
      </div>

      <div className="ib-grid">
        {indices.map((idx) => {
          const change = parseFloat(idx.change)         || 0;
          const pct    = parseFloat(idx.change_percent) || 0;
          const isUp   = change >= 0;
          const price  = idx.price
            ? Number(idx.price).toLocaleString("en-IN", { maximumFractionDigits: 2 })
            : "—";

          return (
            <div
              key={idx.label}
              className={`ib-card ${isUp ? "up" : "down"} ${idx.loading ? "skeleton" : ""}`}
            >
              <div className="ib-card-label">{idx.label}</div>
              {idx.loading ? (
                <div className="ib-skel-price" />
              ) : idx.error ? (
                <div className="ib-card-price err">Unavailable</div>
              ) : (
                <>
                  <div className="ib-card-price">{price}</div>
                  <div className="ib-card-change">
                    <span className="ib-arrow">{isUp ? "▲" : "▼"}</span>
                    {Math.abs(change).toFixed(2)}
                    <span className="ib-pct">({Math.abs(pct).toFixed(2)}%)</span>
                  </div>
                  <div className="ib-bar-track">
                    <div
                      className={`ib-bar-fill ${isUp ? "up" : "down"}`}
                      style={{ width: `${Math.min(Math.abs(pct) * 20, 100)}%` }}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}